import { Fragment } from 'react';
import { format } from 'date-fns-tz';

import { useTaskItem } from 'components/task-item/useTaskItem';
import { FOCO_VISIVEL } from 'utils/foco';
import { getTaskStep } from 'utils/helpers';

export const HUMANOGRAMA = 'https://humanograma.intranet.bb.com.br/';

export interface Responsavel {
  nome: string;
  chave: string;
}

export interface EtapaDaAgenda {
  titulo: string;
  /** Mesmo texto de status que o `TaskItem` mostra na coluna; vazio quando não há. */
  status: string;
  /** Etapa já alcançada; a fechada mostra o cadeado. */
  aberta: boolean;
  /** Etapa em que a realização está (`activeStage` do cartão). */
  atual: boolean;
  /** Gestor da etapa e, no MAD, o par da dupla. */
  responsaveis: Responsavel[];
  /** Datas da etapa no calendário da fase da realização ("dd/MM a dd/MM"); só na agenda da fila. */
  periodo?: string;
  /**
   * Barra da etapa em metades, com as cores que a linha do tempo do `TaskItem` calcula: uma na Validação
   * (`bgColor`), duas nos níveis (`bgColor1`, `bgColor2`). Ex.: Bonificada = verde + vazia; Promovida = verde + verde.
   */
  metades: EstadoDaMetade[];
}

export type EstadoDaMetade = 'vazia' | 'concluida' | 'prazo' | 'devolvida';

/** Cores da linha do tempo do `TaskItem` (`useTaskItem`, `timelineObj`); qualquer outra é o trilho vazio. */
const ESTADO_POR_COR: Record<string, EstadoDaMetade> = {
  '#04D475': 'concluida',
  '#FDC81C': 'prazo',
  '#ED2B48': 'devolvida',
};

function estadoDaCor(cor?: string): EstadoDaMetade {
  return (cor && ESTADO_POR_COR[cor.toUpperCase()]) || 'vazia';
}

type ModeloDoCartao = ReturnType<typeof useTaskItem>;

/** O par do MAD é opcional: a Ação ECOA usa a mesma agenda e não tem MAD. */
export type ModeloDaAgenda = Pick<ModeloDoCartao, 'managersByStep' | 'timeline' | 'activeStage' | 'capitalizeWords'> &
  Partial<Pick<ModeloDoCartao, 'isMAD' | 'getManagerMAD'>>;

function responsavel(pessoa: any, capitalizar: (texto: string) => string): Responsavel | null {
  if (!pessoa?.nome_guerra) return null;
  return { nome: capitalizar(pessoa.nome_guerra), chave: pessoa.chave };
}

/** Etapas da régua do cartão a partir do modelo do `TaskItem` (RDN-08): mesmos títulos, textos e responsáveis. */
export function etapasDaAgenda(modelo: ModeloDaAgenda): EtapaDaAgenda[] {
  return (modelo.managersByStep ?? []).map(etapa => {
    const linha = modelo.timeline(etapa);
    const gestor = linha?.gestor;
    const parDoMAD = gestor && modelo.isMAD ? modelo.getManagerMAD?.(gestor) : undefined;

    return {
      titulo: etapa.stageTitle,
      status: linha?.status || '',
      aberta: !!linha?.opened,
      atual: modelo.activeStage === etapa.stageNumber,
      metades:
        etapa.stageNumber === 0
          ? [estadoDaCor(linha?.bgColor)]
          : [estadoDaCor(linha?.bgColor1), estadoDaCor(linha?.bgColor2)],
      responsaveis: [responsavel(gestor, modelo.capitalizeWords), responsavel(parDoMAD, modelo.capitalizeWords)].filter(
        (pessoa): pessoa is Responsavel => !!pessoa,
      ),
    };
  });
}

/** Campos de data, no calendário da fase (`calendar_v2`), de cada etapa da agenda da fila. */
export const DATAS_DA_ETAPA: Record<string, [string, string]> = {
  Validação: ['date_evaluation_step2_start', 'date_evaluation_step2_finish'],
  'Nível 1': ['date_evaluation_step3_start', 'date_evaluation_step3_finish'],
  'Nível 2': ['date_evaluation_step4_start', 'date_evaluation_step4_finish'],
  'Pit Stop': ['date_evaluation_pitStop_start', 'date_evaluation_pitStop_finish'],
  'Nível 3': ['date_evaluation_step5_start', 'date_evaluation_step5_finish'],
};

/** Etapa do Pit Stop no `getTaskStep` (a mesma que o cartão usa). */
const PASSO_DO_PIT_STOP = 4.1;

export function periodoDa(titulo: string, fase: any): string | undefined {
  const [inicio, fim] = DATAS_DA_ETAPA[titulo];
  if (!fase?.[inicio] || !fase?.[fim]) return undefined;
  return `${format(new Date(fase[inicio]), 'dd/MM')} a ${format(new Date(fase[fim]), 'dd/MM')}`;
}

export type ModeloDaAgendaDaFila = ModeloDaAgenda & Pick<ModeloDoCartao, 'data' | 'stepTask'>;

/**
 * Agenda do cartão da fila (RDN-50, premissa "Agenda do cartão"): as quatro etapas do `TaskItem`, com o Pit Stop
 * entre o Nível 2 e o Nível 3 quando a fase da realização tem datas de Pit Stop, e o período de cada etapa. O Pit
 * Stop não tem texto de status próprio no `TaskItem`; fica aberto quando a realização passou por ele
 * (`prosseguir_gst`) e é a etapa atual enquanto o `getTaskStep` a coloca nele.
 */
export function etapasDaAgendaDaFila(modelo: ModeloDaAgendaDaFila): EtapaDaAgenda[] {
  const fase = modelo.stepTask;
  const passo = getTaskStep(modelo.data);
  const noPitStop = passo === PASSO_DO_PIT_STOP;
  const etapas = etapasDaAgenda(modelo).map(etapa => ({
    ...etapa,
    atual: etapa.atual && !(noPitStop && etapa.titulo === 'Nível 3'),
    periodo: periodoDa(etapa.titulo, fase),
  }));
  const periodoDoPitStop = periodoDa('Pit Stop', fase);
  if (!periodoDoPitStop) return etapas;

  const passouDoPitStop = typeof modelo.data?.prosseguir_gst === 'string' && typeof passo === 'number' && passo > PASSO_DO_PIT_STOP;
  const pitStop: EtapaDaAgenda = {
    titulo: 'Pit Stop',
    // O `TaskItem` não tem texto para o Pit Stop; "Concluído" evita que a barra cheia seja a única indicação (WCAG 1.4.1).
    status: passouDoPitStop ? 'Concluído' : '',
    aberta: typeof modelo.data?.prosseguir_gst === 'string' && typeof passo === 'number' && passo >= PASSO_DO_PIT_STOP,
    atual: noPitStop,
    // Sem cor própria no `TaskItem`: cheio quando a realização já passou do Pit Stop.
    metades: [passouDoPitStop ? 'concluida' : 'vazia'],
    responsaveis: [],
    periodo: periodoDoPitStop,
  };
  const posicaoDoNivel3 = etapas.findIndex(etapa => etapa.titulo === 'Nível 3');
  return [...etapas.slice(0, posicaoDoNivel3), pitStop, ...etapas.slice(posicaoDoNivel3)];
}

/** Status que pede atenção (devolvida, prazo vencido): a pílula fica em tom de alerta. */
const PEDE_ATENCAO = /devolvida|prazo expirado/i;

const FUNDO_DA_METADE: Record<EstadoDaMetade, string> = {
  vazia: 'bg-surface-200',
  concluida: 'bg-brand-green',
  prazo: 'bg-brand-yellow',
  devolvida: 'bg-urgent',
};

/** Barra decorativa: o estado da etapa já está no texto logo abaixo dela. */
function BarraDaEtapa({ metades }: { metades: EstadoDaMetade[] }) {
  return (
    <div aria-hidden="true" className="flex h-2 w-full gap-px overflow-hidden rounded-jj-pill">
      {metades.map((estado, indice) => (
        <span key={indice} data-estado={estado} className={`h-full flex-1 ${FUNDO_DA_METADE[estado]}`} />
      ))}
    </div>
  );
}

function Cadeado() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="shrink-0">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export interface AgendaDaRealizacaoProps {
  etapas: EtapaDaAgenda[];
}

/** Colunas no desktop: duas etapas nas Ações ECOA, quatro no participante, cinco na fila com Pit Stop. */
const COLUNAS: Record<number, string> = { 2: 'md:grid-cols-2', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5' };

/** Régua de etapas do cartão (Validação, Nível 1, Nível 2, [Pit Stop,] Nível 3) com a barra do design. */
export default function AgendaDaRealizacao({ etapas }: AgendaDaRealizacaoProps) {
  if (!etapas.length) return null;

  return (
    <ol
      aria-label="Etapas da apreciação"
      className={`m-0 grid list-none grid-cols-2 gap-3 p-0 ${COLUNAS[etapas.length] ?? 'md:grid-cols-4'}`}
    >
      {etapas.map(etapa => (
        <li key={etapa.titulo} aria-current={etapa.atual ? 'step' : undefined} className="min-w-0">
          <p
            className={`m-0 mb-1.5 flex items-center gap-1 text-[10px] font-bold ${
              etapa.aberta ? 'text-brand-blue-dark' : 'text-content-soft'
            }`}
          >
            {!etapa.aberta && <Cadeado />}
            <span>{etapa.titulo}</span>
            {!etapa.aberta && <span className="sr-only">(bloqueada)</span>}
          </p>
          <BarraDaEtapa metades={etapa.metades} />
          {etapa.periodo && <p className="m-0 mt-1.5 text-[10px] font-semibold text-content-soft">{etapa.periodo}</p>}
          {etapa.status && <p className={`m-0 mt-1.5 text-[11px] text-content ${etapa.atual ? 'font-bold' : ''}`}>{etapa.status}</p>}
          {etapa.responsaveis.length > 0 && (
            <div className="mt-1 text-[10px] text-content-soft">
              {etapa.responsaveis.map((pessoa, indice) => (
                <Fragment key={pessoa.chave}>
                  {indice > 0 && ', '}
                  <a
                    href={`${HUMANOGRAMA}${pessoa.chave}`}
                    target="_blank"
                    rel="noreferrer"
                    className={`text-link hover:text-link-hover ${FOCO_VISIVEL}`}
                  >
                    {pessoa.nome}
                  </a>
                </Fragment>
              ))}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

/** Fundo e texto da pílula pelo status (design): rosa claro aguardando, cinza com problema, verde no resto. */
function corDaPilula(status: string): string {
  if (/aguardando/i.test(status)) return 'bg-brand-pink-light text-brand-navy';
  // SPEC_DEVIATION: a pílula neutra usa `content` (o `text-soft` do design dá 4,1:1 no `surface-200`), como na Minha Equipe.
  if (PEDE_ATENCAO.test(status)) return 'bg-surface-200 text-content';
  return 'bg-brand-green text-brand-navy';
}

/** Pílula com o status da etapa atual da realização; sem status, nada. */
export function PilulaDaEtapa({ status }: { status: string | undefined }) {
  if (!status) return null;

  return (
    <span
      className={`whitespace-nowrap rounded-jj-pill px-3 py-[5px] text-[9.5px] font-bold uppercase tracking-[0.04em] ${corDaPilula(status)}`}
    >
      {status}
    </span>
  );
}
